import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import useApi from "../../hooks/useApi";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import { getId, idEquals } from "../../utils/idHelpers";

const StorageLocations = ({ clientId, projectId }) => {
  const { getData, postData } = useApi();
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    accountNumber: "",
    warehouseCode: "",
    addressStreet: "",
    addressCity: "",
    addressProvince: "",
    addressPostalCode: "",
    capacity: "",
    userId: "",
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load storage locations
      const locationResponse = await getData({
        entityName: "StorageLocation",
        action: "getWithInventorySummary",
        filter: { projectId },
      });

      if (locationResponse.isSuccessful) {
        setLocations(locationResponse.data || []);
      }

      // Load users (reps) for assignment dropdown
      const userResponse = await getData({
        entityName: "User",
        action: "get",
        filter: { isActive: true },
      });
      if (userResponse.isSuccessful) {
        setUsers(userResponse.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location = null) => {
    setSaveError("");
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name || "",
        accountNumber: location.accountNumber || "",
        warehouseCode: location.warehouseCode || "",
        addressStreet: location.address?.street || "",
        addressCity: location.address?.city || "",
        addressProvince: location.address?.province || "",
        addressPostalCode: location.address?.postalCode || "",
        capacity: location.capacity || "",
        userId: getId(location.userId) || getId(location.assignedRep) || "",
        isActive: location.isActive !== false,
      });
    } else {
      setEditingLocation(null);
      setFormData({
        name: "",
        accountNumber: "",
        warehouseCode: "",
        addressStreet: "",
        addressCity: "",
        addressProvince: "",
        addressPostalCode: "",
        capacity: "",
        userId: "",
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLocation(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaveError("");
      const editingLocationId = getId(editingLocation);
      if (editingLocation && !editingLocationId) {
        setSaveError(
          "Unable to save this locker because its ID is missing. Please refresh and try again.",
        );
        return;
      }

      const data = {
        name: formData.name,
        accountNumber: formData.accountNumber || null,
        warehouseCode: formData.warehouseCode || null,
        address: {
          street: formData.addressStreet,
          city: formData.addressCity,
          province: formData.addressProvince,
          postalCode: formData.addressPostalCode,
        },
        capacity: formData.capacity,
        userId: formData.userId || null,
        isActive: formData.isActive,
        clientId,
        projectId,
      };

      console.log("data:", data);

      const response = await postData({
        entityName: "StorageLocation",
        action: editingLocation ? "update" : "add",
        id: editingLocation ? editingLocationId : undefined,
        data: editingLocation ? { ...data, id: editingLocationId } : data,
      });

      console.log("response: ", response);
      if (response.isSuccessful) {
        handleCloseDialog();
        loadData();
      } else {
        setSaveError(response?.message || "Unable to save locker.");
      }
    } catch (error) {
      setSaveError(error?.message || "Unable to save locker.");
      console.error("Error saving storage location:", error);
    }
  };

  const handleDelete = async (location) => {
    const locationId = getId(location);
    if (!locationId) return;
    try {
      const response = await postData({
        entityName: "StorageLocation",
        action: "delete",
        data: { id: locationId },
      });

      if (response.isSuccessful) {
        loadData();
      }
    } catch (error) {
      console.error("Error deleting storage location:", error);
    }
  };

  const getAssignedRepName = (userId) => {
    const rep = users.find((u) => idEquals(u, userId));
    if (!rep) return "Unassigned";
    if (rep.name) return rep.name;
    return rep.legalName || "Unassigned";
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6">Lockers</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Locker
        </Button>
      </Box>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Account #</TableCell>
              <TableCell>Warehouse Code</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Capacity</TableCell>
              <TableCell>Current Qty</TableCell>
              <TableCell>Assigned Rep</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                >
                  <Typography color="text.secondary">
                    No lockers found. Click "Add Locker" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              locations.map((location) => (
                <TableRow key={getId(location)}>
                  <TableCell>{location.name}</TableCell>
                  <TableCell>{location.accountNumber || "-"}</TableCell>
                  <TableCell>{location.warehouseCode || ""}</TableCell>
                  <TableCell>
                    {location.address && (
                      <>
                        {location.address.street && (
                          <>
                            {location.address.street}
                            <br />
                          </>
                        )}
                        {location.address.city && (
                          <>{location.address.city}, </>
                        )}
                        {location.address.province && (
                          <>{location.address.province} </>
                        )}
                        {location.address.postalCode}
                      </>
                    )}
                  </TableCell>
                  <TableCell>{location.capacity || 0}</TableCell>
                  <TableCell>
                    {location.totalQuantity ?? location.currentQuantity ?? 0}
                  </TableCell>
                  <TableCell>{getAssignedRepName(location.userId)}</TableCell>
                  <TableCell>
                    <Chip
                      label={location.isActive ? "Active" : "Inactive"}
                      color={location.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(location)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(location)}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingLocation ? "Edit Locker" : "Add Locker"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Account #"
              value={formData.accountNumber}
              onChange={(e) => handleChange("accountNumber", e.target.value)}
              fullWidth
            />
            <TextField
              label="Warehouse Code"
              value={formData.warehouseCode}
              onChange={(e) => handleChange("warehouseCode", e.target.value)}
              fullWidth
            />
            <TextField
              label="Street Address"
              value={formData.addressStreet}
              onChange={(e) => handleChange("addressStreet", e.target.value)}
              fullWidth
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="City"
                value={formData.addressCity}
                onChange={(e) => handleChange("addressCity", e.target.value)}
                fullWidth
              />
              <TextField
                label="Province"
                value={formData.addressProvince}
                onChange={(e) =>
                  handleChange("addressProvince", e.target.value)
                }
                sx={{ width: "40%" }}
              />
            </Box>
            <TextField
              label="Postal Code"
              value={formData.addressPostalCode}
              onChange={(e) =>
                handleChange("addressPostalCode", e.target.value)
              }
              sx={{ width: "50%" }}
            />
            <TextField
              label="Capacity"
              value={formData.capacity}
              onChange={(e) => handleChange("capacity", e.target.value)}
              placeholder="e.g., 100 sq ft, 50 cubic ft, 10 pallets"
              fullWidth
            />
            <Autocomplete
              options={users}
              getOptionLabel={(option) => {
                const displayName = option.name || option.legalName;
                return `${displayName || "Unknown"} (${option.email || "no email"})`;
              }}
              value={users.find((u) => idEquals(u, formData.userId)) || null}
              onChange={(e, newValue) =>
                handleChange("userId", getId(newValue) || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned Rep"
                  placeholder="Search for a rep..."
                />
              )}
              fullWidth
              isOptionEqualToValue={(option, value) => idEquals(option, value)}
            />
            <TextField
              label="Status"
              select
              value={formData.isActive}
              onChange={(e) => handleChange("isActive", e.target.value)}
              fullWidth
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </TextField>
            {!!saveError && <Typography color="error">{saveError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog_YesNo
        open={!!deleteTarget}
        title="Delete Locker"
        message={`Are you sure you want to delete locker "${deleteTarget?.name || ""}"?`}
        onClose={() => setDeleteTarget(null)}
        onNo={() => setDeleteTarget(null)}
        onYes={async () => {
          const target = deleteTarget;
          setDeleteTarget(null);
          await handleDelete(target);
        }}
      />
    </Box>
  );
};

export default StorageLocations;
